import ConnectingToOrg from 'components/Loaders/ConnectingToOrg';
import SigningIn from 'components/Loaders/SigningIn';
import SystemError from 'components/SystemError';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { getPrincipal } from 'utils/getPrincipal';
import { LoginPageHandlerType } from 'utils/types';

import { Button, designTokens, Flex, TextField } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { User } from '@project44/ui-utils';

import { useDetermineSso } from '../../../hooks/useDetermineSso';
import { useLoginSso } from '../../../hooks/useLoginSso';
import { LoginPageResolverEnum } from '../../../utils/constants';
import {
  getRedirectUriFromQueryParams,
  redirectAfterLogin,
} from '../../../utils/redirectUri';
import { LoginFormValidationSchema } from './validationSchema';

const LoginWithSso = ({
  loginPageHandler,
}: LoginPageHandlerType): JSX.Element => {
  const { t } = useTranslation();

  const [determineSsoDataState, determineSsoDataHandler] = useDetermineSso();
  const [loginDataState, loginDataHandler] = useLoginSso();
  const [hasRun, setHasRun] = useState(false);

  const loginErrorCode =
    loginDataState?.error?.statusCode ||
    determineSsoDataState?.error?.statusCode;

  const connectingToOrg: boolean = !!(
    determineSsoDataState?.isLoading || determineSsoDataState?.data
  );

  const formik = useFormik({
    initialValues: {
      username: '',
      idpId: '',
    },
    validationSchema: LoginFormValidationSchema,
    validateOnChange: false,
    onSubmit: (values) => {
      determineSsoDataHandler({
        idpId: values.idpId,
        username: values.username,
      });
    },
  });

  const checkAuthorizationCodeAndState = async () => {
    const params = new URLSearchParams(window.location.search);
    const maybeOktaResponseStateString = params.get('state');
    const sessionState = sessionStorage.getItem('state');
    if (
      params.get('code')?.length &&
      maybeOktaResponseStateString === sessionState
    ) {
      const authorizationCode = params.get('code');
      await loginDataHandler({ code: authorizationCode });
    }
  };

  const determineSsoHandlerBasedOnParams = () => {
    const params = new URLSearchParams(window.location.search);
    const idpId = params.get('idpId');
    const username = params.get('username');
    if (!!username || !!idpId) {
      formik.values.idpId = idpId ?? '';
      formik.values.username = username ?? '';
      determineSsoDataHandler({ idpId: idpId, username: username });
    }
  };

  const checkSession = async () => {
    const redirectUriFromParams = getRedirectUriFromQueryParams();
    redirectUriFromParams &&
      localStorage.setItem('redirectUri', redirectUriFromParams);
    const principalData = await getPrincipal();
    if (principalData) {
      redirectAfterLogin(principalData);
    }
  };

  if (!hasRun) {
    // Code here will run only once
    checkSession();
    determineSsoHandlerBasedOnParams();
    checkAuthorizationCodeAndState();

    // Update the flag to prevent re-execution
    setHasRun(true);
  }

  useEffect(() => {
    if (determineSsoDataState?.data !== undefined) {
      const redirectUriFromParams = getRedirectUriFromQueryParams();
      localStorage.setItem('redirectUri', redirectUriFromParams);
      if (determineSsoDataState?.data?.redirectURL?.length) {
        const maybeBase64Token: string =
          determineSsoDataState?.data?.state || '';
        sessionStorage.setItem('state', maybeBase64Token);
        window.location.assign(determineSsoDataState.data.redirectURL);
      } else if (determineSsoDataState?.data?.redirectURL === null) {
        loginPageHandler({
          strategy: LoginPageResolverEnum.LOGIN_NON_SSO,
          username: formik.values.username,
        });
      }
    }
  }, [determineSsoDataState?.data, formik.values.username, loginPageHandler]);

  if (loginDataState?.data && !loginDataState?.isLoading) {
    redirectAfterLogin(loginDataState.data as User);
  }

  return (
    <Flex
      css={{
        flexDirection: 'column',
      }}
    >
      {connectingToOrg && <ConnectingToOrg />}
      {loginErrorCode && <SystemError statusCode={loginErrorCode} />}
      {!!(loginDataState?.isLoading || loginDataState?.data) ? (
        <SigningIn />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <TextField
            data-testid="email-input"
            helperText={formik.errors.username}
            placeholder={t('loginPage.emailInputFieldPlaceholder')}
            label={t('loginPage.emailInputFieldLabel')}
            value={formik.values.username}
            isDisabled={connectingToOrg}
            isRequired={true}
            validationState={formik.errors.username ? 'invalid' : 'valid'}
            inputProps={{
              name: 'email',
              autoComplete: 'email',
            }}
            onChange={(currentValue) => {
              formik.errors.username && formik.setErrors({ username: '' });
              formik.setFieldValue('username', currentValue);
            }}
          />
          <Button
            type="submit"
            variant="brand"
            isDisabled={connectingToOrg}
            css={{
              width: '100%',
              position: 'relative',
              marginTop: designTokens.spaceMedium,
            }}
          >
            {connectingToOrg
              ? t('signInProcessStatus.signingIn')
              : t('loginPage.logIn')}
          </Button>
        </form>
      )}
    </Flex>
  );
};

export default LoginWithSso;
